import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { VonPageResponseModel } from '@von-development-studio/angular-rest-service';
import { VonConfirmationDialogService } from '@von-development-studio/primeng-confirmation-dialog';
import { VonMessageService } from '@von-development-studio/primeng-message-service';
import { FilterMetadata, SelectItem } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { BaseService } from 'src/app/core/base/base.service';
import { RestService } from 'src/app/core/service/rest.service';
import { CommonPatientModel } from 'src/app/model/common/patient';
import { CommonPatientViewModel } from 'src/app/model/common/patient.view';
import { KhuyanaSelectItemModel } from 'src/app/model/core/khuyana-select-item.model';
import { commonPatientActions } from 'src/app/store/common/patient/patient.action';
import { commonPersonActions } from 'src/app/store/common/person/person.action';
import { environment } from 'src/environments/environment';
import { AdminLocationService } from '../../admin/location/admin-location.service';
import { PersonService } from '../person/person.service';
import { PATIENT } from './patient.constants';

@Injectable({
  providedIn: 'root',
})
export class PatientService extends BaseService {
  readonly screen = PATIENT;

  constructor(
    protected store: Store<any>,
    protected rest: RestService,
    protected dialogService: DialogService,
    protected messageService: VonMessageService,
    protected confirmationDialogService: VonConfirmationDialogService,
    protected locationService: AdminLocationService,
    protected personService: PersonService
  ) {
    super();
  }

  readStore = () => {
    const locations: Observable<SelectItem<KhuyanaSelectItemModel>[]> =
      this.store.select('admin', 'location', 'queryResult');
    const patientData: Observable<CommonPatientModel> = this.store.select(
      'common',
      'patient',
      'data'
    );
    const residence: Observable<SelectItem<KhuyanaSelectItemModel>> =
      this.store.select('common', 'patient', 'residence');
    return { locations, patientData, residence };
  };

  readLocations = (query: string) =>
    this.locationService.readLocationSelectors(query);

  readPatientLazyLoad = (): Observable<
    VonPageResponseModel<CommonPatientViewModel>
  > => this.store.select('common', 'patient', 'result');

  getPersonById = this.personService.getPersonById;

  getPatientByPerson = (person: number): Observable<CommonPatientModel> =>
    this.rest
      .get<CommonPatientModel>({
        url: `${environment.endpoints.common}/patient/by/${person}`,
      })
      .pipe(tap(this.patientSuccess, this.patientFailure));

  getPatientById = (id: number): Observable<CommonPatientModel> =>
    this.rest
      .get<CommonPatientModel>({
        url: `${environment.endpoints.common}/patient/${id}`,
      })
      .pipe(tap(this.patientSuccess, this.patientFailure));

  getPatientViewById = (id: number) =>
    this.rest.get<CommonPatientViewModel>({
      url: `${environment.endpoints.common}/patient/{id}/view`,
      urlParams: { id },
    });

  getPatients = (
    page: number,
    size: number,
    nameQuery?: FilterMetadata,
    documentQuery?: FilterMetadata
  ): Observable<VonPageResponseModel<CommonPatientViewModel>> => {
    const body: any = { page, size };
    if (nameQuery) {
      body.nameQuery = nameQuery.value;
    }
    if (documentQuery) {
      body.documentQuery = documentQuery.value;
    }

    return this.rest
      .get<VonPageResponseModel<CommonPatientViewModel>>({
        url: `${environment.endpoints.common}/patient`,
        params: body,
      })
      .pipe(
        tap((result) =>
          this.store.dispatch(commonPatientActions.setResultAction(result))
        )
      );
  };

  patientSuccess = (patient: CommonPatientModel) => {
    let patientState: CommonPatientModel;
    if (patient) {
      patientState = {
        ...patient,
        isNew: false,
      };
    } else {
      patientState = {
        isNew: true,
      };
    }
    this.store.dispatch(commonPatientActions.setPatientAction(patientState));
    if (patientState.residence) {
      this.rest
        .get<KhuyanaSelectItemModel>({
          url: `${environment.endpoints.admin}/location/selector/${patient.residence}`,
        })
        .subscribe((location) =>
          this.store.dispatch(
            commonPatientActions.setResidenceAction({
              label: location.name,
              value: location,
            })
          )
        );
    } else {
      this.store.dispatch(
        commonPatientActions.setResidenceAction({ value: {} })
      );
    }
  };

  patientFailure = () =>
    this.store.dispatch(commonPatientActions.setPatientAction({ isNew: true }));

  setPatientInformation = (patient: CommonPatientModel): CommonPatientModel => {
    if (patient.isNew) {
      return {
        ...patient,
        personHelp: {},
      };
    }

    return {
      ...patient,
      creationDate: patient.creationDate
        ? new Date(patient.creationDate)
        : undefined,
      updateDate: patient.updateDate ? new Date(patient.updateDate) : undefined,
    };
  };

  save = (patient: CommonPatientModel): Observable<CommonPatientViewModel> =>
    this.rest
      .post<CommonPatientViewModel>({
        url: `${environment.endpoints.common}/patient`,
        body: patient,
      })
      .pipe(
        tap(() =>
          this.messageService.addSuccess(this.screen.labels.saveSuccess)
        )
      );

  openPatientDialog = (
    isNew: boolean = true,
    component: any
  ): DynamicDialogRef => {
    if (isNew) {
      this.personService.setPerson();
      this.patientFailure();
    }
    const dialogRef = this.dialogService.open(component, {
      data: {
        save: this.save,
      },
      header: isNew ? this.screen.actions.add : this.screen.actions.edit,
      closeOnEscape: true,
      width: '75%',
    });
    return dialogRef;
  };

  patientOnClose = (modalResult: any) => {
    if (modalResult) {
      const action = modalResult.isNew
        ? commonPatientActions.addResultAction
        : commonPatientActions.patchResultAction;
      this.store.dispatch(action(modalResult));
    }
    this.store.dispatch(commonPatientActions.setPatientDefaultAction(true));
    this.store.dispatch(commonPersonActions.setPersonDefaultAction(true));
  };
}
